import { BackButton, Size, TextButton } from "@laerdal/life-react-components";
import { MouseEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { posthog } from "posthog-js";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import PaligoContent from "../../../commonComponents/PaligoContent";

interface TroubleshootingProps {
  htmlContent: string;
  onDismiss: () => void;
  onReportProblem: () => void;
}

const ActionsSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const Troubleshooting = ({ htmlContent, onDismiss, onReportProblem }: TroubleshootingProps) => {
  const { t } = useTranslation('landingPage');
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Intercept clicks on in-app `/articles/...` links inside Paligo-rendered HTML so navigation
  // stays inside the SPA (no full page reload). External links, modifier-clicks, target="_blank"
  // and any non-article paths fall through to default browser behaviour.
  const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const anchor = (e.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Only intercept root-relative article links (with or without a 2-char market prefix).
    if (!/^\/(?:[a-z]{2}\/)?articles\//.test(href)) return;

    e.preventDefault();
    posthog?.capture?.('SUPP-Troubleshooting InContentLinkClick', {
      target: href,
    });
    navigate(href);
  };

  return (
    <>
       <BackButton size={Size.Small} onClick={onDismiss}>
          {t('Back')}
      </BackButton>
      <PaligoContent onClick={handleContentClick} dangerouslySetInnerHTML={{ __html: htmlContent }} />
      <ActionsSection>
        <TextButton variant="secondary" onClick={onReportProblem}>{t('Report a problem')}</TextButton>
        <TextButton variant="primary" onClick={onDismiss}>{t('Dismiss')}</TextButton>
      </ActionsSection>
    </>
  );
};

export default Troubleshooting;